import { useState, useEffect, useRef, ChangeEvent } from "react";
import { format } from "date-fns";
import { DateHookProps } from "../../types";
import { getEligibleDate } from "../utils/getEligibleDate";
import {
  evaluateDate,
  handleDateInputChange,
  sanitizeMobile,
  formatMonth,
  formatDay,
  formatYear,
  ERROR_MESSAGES,
  ErrorReason,
} from "../utils/manageInputDate";
import { formatDateMMDDYYYY, isISODate } from "@/utils/dates";

export const useDatePicker = ({
  range: { minEligibleDate, maxEligibleDate },
  dateTrip,
  dateReturn,
  dateValue,
  launchValidation,
  persistValue,
  isTablet,
}: DateHookProps) => {
  const ISO_LIKE_REGEX = /^\d{4}-\d{2}-\d{2}(?:T.*)?$/;
  const parseLocalDateFromISO = (iso?: string): Date | undefined => {
    if (!iso || !ISO_LIKE_REGEX.test(iso)) return undefined;
    const [datePart] = iso.split("T");
    const [y, m, d] = datePart.split("-").map((v) => parseInt(v, 10));
    if (!y || !m || !d) return undefined;
    // Construct a local Date without going through UTC parsing of `new Date('YYYY-MM-DD')`
    return new Date(y, m - 1, d);
  };
  const parseLocalDateFromUS = (us?: string): Date | undefined => {
    if (!us) return undefined;
    const { isValid } = evaluateDate(us);
    if (!isValid) return undefined;
    const [mm, dd, yyyy] = us.split("/");
    const y = parseInt(yyyy, 10);
    const m = parseInt(mm, 10);
    const d = parseInt(dd, 10);
    if (!y || !m || !d) return undefined;
    return new Date(y, m - 1, d);
  };

  const dateString = dateValue
    ? isISODate(dateValue) || ISO_LIKE_REGEX.test(dateValue)
      ? formatDateMMDDYYYY(dateValue)
      : dateValue
    : "";
  const parsedDate =
    isISODate(dateValue) || ISO_LIKE_REGEX.test(dateValue)
      ? parseLocalDateFromISO(dateValue)
      : parseLocalDateFromUS(dateString);
  const prevValue = useRef<string>(dateValue || "");

  /*--------------- STATE VALUES ---------------
    --------------------------------------------*/

  // React-day-picker
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [month, setMonth] = useState(parsedDate || new Date());
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(parsedDate);

  // Desktop input
  const [inputDate, setInputDate] = useState<string>(dateString);

  // Mobile input
  const [initial_mm = "", initial_dd = "", initial_yyyy = ""] = dateString?.split("/") ?? [];
  const [inputMonth, setInputMonth] = useState<string>(initial_mm);
  const [inputDay, setInputDay] = useState<string>(initial_dd);
  const [inputYear, setInputYear] = useState<string>(initial_yyyy);

  // Fields refs
  const mobileRef = useRef<HTMLInputElement>(null);

  // Validation and range errors
  const [inputError, setInputError] = useState<string>("");

  /*-------------- DATE LIMITS -----------------
    --------------------------------------------*/

  const { minDateAvailable, maxDateAvailable } = getEligibleDate(
    minEligibleDate,
    maxEligibleDate,
    dateTrip,
    dateReturn
  );

  /*---- SYNC RHF -> Input (External changes)----
    ---------------------------------------------*/

  useEffect(() => {
    if (dateValue === "") setSelectedDate(undefined);
    if (dateValue && (isISODate(dateValue) || ISO_LIKE_REGEX.test(dateValue))) {
      const newDateString = formatDateMMDDYYYY(dateValue);
      setInputDate(newDateString);
      const localDate = parseLocalDateFromISO(dateValue);
      if (localDate) {
        setSelectedDate(localDate);
        setMonth(localDate);
      }
    } else {
      const newDateString = dateValue ? dateValue : "";
      setInputDate(newDateString);
    }
  }, [dateValue]);

  /*-------------- MOBILE HELPERS ---------------
    ---------------------------------------------*/

  const updateMobileValues = (newValue: string) => {
    const [mm = "", dd = "", yyyy = ""] = newValue.split("/");
    setInputMonth(mm);
    setInputDay(dd);
    setInputYear(yyyy);
  };

  const buildDateString = (MM: string, DD: string, YYYY: string) =>
    [MM, DD, YYYY].filter(Boolean).join("/");

  /*---------------- CORE LOGIC -----------------
    ---------------------------------------------*/
  const saveDate = (date: Date) => {
    setInputError("");
    // Build ISO from local Y/M/D to avoid timezone shifts
    const yyyy = String(date.getFullYear());
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const iso = `${yyyy}-${mm}-${dd}`;
    const us = `${mm}/${dd}/${yyyy}`;

    setSelectedDate(date);
    setMonth(date);
    setInputDate(us);
    updateMobileValues(us);
    // Save in RHF
    persistValue(iso);
  };

  const handleError = (reason: ErrorReason) => {
    const message = ERROR_MESSAGES[reason] || ERROR_MESSAGES.unknown;
    setInputError(message);
    handleClearInput();
  };

  const processDate = (dateValue: string) => {
    if (dateValue === "") {
      launchValidation();
      return;
    }

    const { isValid, reason } = evaluateDate(dateValue);
    if (!isValid) return handleError(reason as ErrorReason);

    const date = parseLocalDateFromUS(dateValue);
    if (!date) return handleError("non-valid-values");
    if (date > maxDateAvailable) return handleError("max-limit");
    if (date < minDateAvailable) return handleError("min-limit");

    saveDate(date);
    launchValidation();
  };

  /*------------- HANDLERS DESKTOP -------------- 
    ---------------------------------------------*/

  const handleDayPickerSelect = (date: Date | undefined) => {
    if (!date) {
      persistValue("");
      setSelectedDate(undefined);
    } else {
      // Normalize to local date at midnight to avoid any TZ-induced shifts
      const local = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      saveDate(local);
      launchValidation();
    }
    setIsOpen(false);
  };

  const handleBlur = (e?: React.FocusEvent<HTMLInputElement>) => {
    if (!isTablet && isOpen && e) {
      e.preventDefault();
      e.stopPropagation();
      e.target.focus();
      return;
    }
    processDate(inputDate);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputError("");
    const value = handleDateInputChange(e.target.value, prevValue.current);

    if (value.length === 10) {
      if (evaluateDate(value).isValid) {
        const date = parseLocalDateFromUS(value);
        if (!date) {
          setInputDate(value);
          prevValue.current = value;
          return;
        }
        setSelectedDate(date);
        setMonth(date);
        saveDate(date);
      }
    }

    setInputDate(value);
    prevValue.current = value;
  };

  /*------------- HANDLERS MOBILE --------------- 
    ---------------------------------------------*/
  const handleChangeMobile = ({
    month,
    day,
    year,
  }: {
    month?: string;
    day?: string;
    year?: string;
  }) => {
    setInputError("");

    if (month !== undefined) {
      let m = formatMonth(sanitizeMobile(month));
      setInputMonth(m);
      return;
    }

    if (day !== undefined) {
      const numMonth = inputMonth ? parseInt(inputMonth, 10) || undefined : undefined;
      let d = formatDay(sanitizeMobile(day), numMonth);
      setInputDay(d);
      return;
    }

    if (year !== undefined) {
      let y = formatYear(sanitizeMobile(year));
      setInputYear(y);
      return;
    }
  };

  const handleBlurMobile = (e?: React.FocusEvent) => {
    const MM = inputMonth ? inputMonth.padStart(2, "0") : "";
    const DD = inputDay ? inputDay.padStart(2, "0") : "";
    const YYYY = inputYear || "";

    if (MM && MM !== inputMonth) setInputMonth(MM);
    if (DD && DD !== inputDay) setInputDay(DD);

    // run after blur event completes to detect new active element
    setTimeout(() => {
      if (mobileRef.current?.contains(document.activeElement)) return;

      const rawCombined = buildDateString(MM, DD, YYYY);

      // Normalize & format using existing helper (it expects raw + prevValue)
      const normalized = handleDateInputChange(rawCombined, prevValue.current);

      // Update the visible source-of-truth
      setInputDate(normalized);
      updateMobileValues(normalized);
      prevValue.current = normalized;

      processDate(normalized);
    }, 0);
  };

  /*--------------- CLEAR/RESET ----------------- 
    ---------------------------------------------*/

  const handleClearInput = () => {
    persistValue("");
    updateMobileValues("");
    setInputDate("");
    setSelectedDate(undefined);
    setMonth(new Date());
    prevValue.current = "";
  };

  const handleReset = () => {
    persistValue("");
    handleClearInput();
    setInputError("");
  };

  return {
    // state
    dateValue,
    isOpen,
    setIsOpen,
    inputDate,
    inputError,
    month,
    setMonth,
    selectedDate,

    // mobile states
    inputMonth,
    inputDay,
    inputYear,

    //refs
    mobileRef,

    // handlers
    handleChange,
    handleBlur,
    handleDayPickerSelect,
    handleChangeMobile,
    handleBlurMobile,
    handleReset,

    // utils
    updateMobileValues,

    // date limits
    minDateAvailable,
    maxDateAvailable,
  };
};
